import { ProfileInstallation } from '../shared/services/backend'

/**
 * Type guard to check if installation data is complete and valid for production-type decisions.
 * This prevents race conditions where meterUnits or meterPointTypeCode may be undefined during state transitions.
 *
 * @param installation - The installation to check
 * @returns true if the installation has all required fields for production-type determination
 */
export function hasCompleteInstallationData(
    installation: ProfileInstallation | null | undefined
): installation is ProfileInstallation {
    return (
        installation !== null &&
        installation !== undefined &&
        // meterUnits must be explicitly defined (can be empty string, but not undefined)
        installation.meterUnits !== undefined &&
        // meterPointTypeCode must be defined to correctly determine if it's a production installation
        installation.meterPointTypeCode !== undefined
    )
}

/**
 * Checks if an installation is an el/varme producer (varmeværk).
 * El/varme producers have meterUnits containing "elproduktion" or "varmeproduktion".
 *
 * @param installation - The installation to check
 * @returns true if the installation is an el/varme producer
 */
export function isElVarmeProducer(installation: ProfileInstallation | null | undefined): boolean {
    const meterUnits = installation?.meterUnits?.toLowerCase() || ''
    return meterUnits.includes('elproduktion') || meterUnits.includes('varmeproduktion')
}

/**
 * Checks if an installation is a biogas production installation.
 * Returns true only when:
 * - meterPointTypeCode = "PROD" from Xellent MeterPointTypeCodePeriods
 * - AND meterUnits does NOT contain "elproduktion" or "varmeproduktion" (el/varme producers are NOT biogas)
 *
 * @param installation - The installation to check
 * @returns true if the installation is a biogas production installation
 */
export function isBiogasProduction(installation: ProfileInstallation | null | undefined): boolean {
    const meterPointTypeCode = installation?.meterPointTypeCode
    return meterPointTypeCode === 'PROD' && !isElVarmeProducer(installation)
}

/**
 * Gets the i18n key for the production label based on meterUnits.
 * Returns the appropriate label for:
 * - Elproduktion only
 * - Varmeproduktion only
 * - Both Elproduktion and Varmeproduktion
 *
 * @param installation - The installation to check
 * @returns The i18n key for the production label, or null if not an el/varme producer
 */
export function getElVarmeProductionLabelKey(installation: ProfileInstallation | null | undefined): string | null {
    const meterUnits = installation?.meterUnits?.toLowerCase() || ''
    const hasElproduktion = meterUnits.includes('elproduktion')
    const hasVarmeproduktion = meterUnits.includes('varmeproduktion')

    if (hasElproduktion && hasVarmeproduktion) {
        return 'userProfile.customerRelations.elVarmeproduktion'
    } else if (hasElproduktion) {
        return 'userProfile.customerRelations.elproduktion'
    } else if (hasVarmeproduktion) {
        return 'userProfile.customerRelations.varmeproduktion'
    }
    return null
}
